import { Button, ModuleWrapper } from '@/components'
import { PageType } from '@/modules/common/consts'
import { MODULE_ICONS } from '@/modules/common/module-icons'
import { useTranslate } from '@ur-solutions/react-hooks'
import { Link, useSearchParams } from 'react-router'
import { ExportCategoryNav } from './export-category-nav'
import type { ExportCategoryId } from './use-export-categories'
import { useExportCategories } from './use-export-categories'

export const ExportsPage: React.FC = () => {
  const translations = useTranslate({
    title: 'common.exports-settings',
    subtitle: 'exports.page.subtitle',
    userExports: 'exports.user-exports',
    noResults: 'common.no-results',
  })

  const categories = useExportCategories()
  const [searchParams, setSearchParams] = useSearchParams()

  const selected =
    categories.find(category => category.id === searchParams.get('category')) ??
    categories[0]

  function selectCategory(id: ExportCategoryId) {
    setSearchParams({ category: id }, { replace: true })
  }

  return (
    <ModuleWrapper
      title={translations.title}
      page={PageType.EXPORTS}
      headerRight={
        <Button
          variant="outline"
          className="h-10 rounded-md px-3 sm:px-4"
          aria-label={translations.userExports}
          asChild
        >
          <Link to="/user-exports">
            <MODULE_ICONS.userExports size={18} strokeWidth={1.5} />
            <span className="hidden sm:inline">{translations.userExports}</span>
          </Link>
        </Button>
      }
    >
      <p className="mb-6 text-sm text-stone-500">{translations.subtitle}</p>

      {!selected ? (
        <div className="rounded-2xl bg-white px-6 py-10 text-center text-sm text-stone-500 shadow-card">
          {translations.noResults}
        </div>
      ) : (
        <div className="grid gap-4 xl:grid-cols-[15rem_minmax(0,1fr)] xl:items-start xl:gap-6">
          <ExportCategoryNav
            categories={categories}
            selected={selected.id}
            onSelect={selectCategory}
          />

          <section className="min-w-0 space-y-4">
            <header className="flex items-start gap-3 px-1">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-stone-700 shadow-card">
                <selected.icon size={20} strokeWidth={1.5} />
              </span>

              <div className="space-y-0.5">
                <h2 className="text-xl font-semibold text-stone-900">
                  {selected.label}
                </h2>
                <p className="text-sm text-stone-500">{selected.description}</p>
              </div>
            </header>

            <div key={selected.id} className="space-y-4">
              {selected.content}
            </div>
          </section>
        </div>
      )}
    </ModuleWrapper>
  )
}
